/**
 * Structural sharing for cached API reads (P-08).
 *
 * Returns `next`, except that every array or plain object inside it which is
 * deep-equal to its counterpart in `previous` is replaced by the previous
 * reference. A poll that returns unchanged data therefore hands React the same
 * objects it already rendered, so memoized rows and `useMemo` derivations skip
 * their work. Only JSON-shaped data (arrays, plain objects, primitives) is
 * shared; anything else is taken from `next` as-is.
 */
export function replaceEqualDeep<T>(previous: unknown, next: T): T {
  if (Object.is(previous, next)) return previous as T;
  const previousIsArray = Array.isArray(previous);
  const nextIsArray = Array.isArray(next);
  if (previousIsArray && nextIsArray) {
    const before = previous as unknown[];
    const after = next as unknown[];
    let equalItems = 0;
    const copy = after.map((item, index) => {
      const shared = index < before.length ? replaceEqualDeep(before[index], item) : item;
      if (index < before.length && shared === before[index]) equalItems++;
      return shared;
    });
    return (before.length === after.length && equalItems === after.length ? before : copy) as T;
  }
  if (!previousIsArray && !nextIsArray && isPlainObject(previous) && isPlainObject(next)) {
    const before = previous as Record<string, unknown>;
    const after = next as Record<string, unknown>;
    const beforeKeys = Object.keys(before);
    const afterKeys = Object.keys(after);
    let equalItems = 0;
    const copy: Record<string, unknown> = {};
    for (const key of afterKeys) {
      const shared = Object.prototype.hasOwnProperty.call(before, key) ? replaceEqualDeep(before[key], after[key]) : after[key];
      copy[key] = shared;
      if (Object.prototype.hasOwnProperty.call(before, key) && shared === before[key]) equalItems++;
    }
    return (beforeKeys.length === afterKeys.length && equalItems === afterKeys.length ? before : copy) as T;
  }
  return next;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object") return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}
